import React, { useState, useEffect } from 'react';
import { useSubscription } from '../hooks/useSubscription';
import { useAuth } from '@/features/auth/stores/auth.store';
import { remoteConfig } from '@/shared/lib/firebase';
import { fetchAndActivate, getValue } from 'firebase/remote-config';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import DashboardLayout from '@/features/dashboard/pages/DashboardLayout';
import { PricingConfig, PricingPlan } from '../types';
import { SubscriptionApiService } from '../service/SubscriptionApiService';
import { BillingToggle } from '../components/BillingToggle';
import { ActiveSubscriptionBanner } from '../components/ActiveSubscriptionBanner';
import { PricingCard } from '../components/PricingCard';

const PricingPage = () => {
    const { subscription, creditBalance, isActive, isPending, loading } = useSubscription();
    const { user } = useAuth();
    const [isYearly, setIsYearly] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);

    const [pricingConfig, setPricingConfig] = useState<PricingConfig | null>(null);

    useEffect(() => {
        const loadRemoteConfig = async () => {
            try {
                await fetchAndActivate(remoteConfig);
                const val = getValue(remoteConfig, 'PRICING_PLANS').asString();
                if (val) setPricingConfig(JSON.parse(val));
            } catch (err) {
                console.error('Error fetching remote config:', err);
                const val = getValue(remoteConfig, 'PRICING_PLANS').asString();
                if (val) setPricingConfig(JSON.parse(val));
            }
        };
        loadRemoteConfig();
    }, []);

    const handleSubscribe = async (planData: PricingPlan) => {
        if (!user) {
            toast.error("Please log in to subscribe");
            return;
        }

        // Do not process if free tier
        if (planData.tier === 'free') {
            return;
        }

        setIsProcessing(true);
        try {
            const planDuration = isYearly ? 'yearly' : 'monthly';
            const razorpayPlanId = isYearly ? planData.razorpayYearlyPlanId : planData.razorpayMonthlyPlanId;

            // Note: Our backend expects { plan: 'monthly'|'yearly' }.
            // We pass planId as well so backend can look up exact Razorpay plan_id in the future.
            const data = await SubscriptionApiService.createSubscription({
                plan: planDuration,
                planId: planData.id,
                razorpayPlanId: razorpayPlanId
            });

            if (data && data.subscription_id && data.key_id) {
                const loadRazorpayScript = () => new Promise((resolve) => {
                    const script = document.createElement('script');
                    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
                    script.onload = () => resolve(true);
                    script.onerror = () => resolve(false);
                    document.body.appendChild(script);
                });

                const loaded = await loadRazorpayScript();
                if (!loaded) {
                    toast.error("Failed to load Razorpay SDK");
                    setIsProcessing(false);
                    return;
                }

                const options = {
                    key: data.key_id,
                    subscription_id: data.subscription_id,
                    name: "Storyverse",
                    description: `${planData.name} ${isYearly ? 'Yearly' : 'Monthly'} Subscription`,
                    handler: function () {
                        toast.success("Payment successful! Your subscription will be activated shortly.");
                        setIsProcessing(false);
                    },
                    modal: {
                        ondismiss: async function () {
                            setIsProcessing(false);
                            toast.error("Subscription payment cancelled.");

                            // Cleanup the pending status in Firestore so UI reverts
                            try {
                                await SubscriptionApiService.cleanupSubscription();
                            } catch (e) {
                                console.error("Failed to cleanup", e);
                            }
                        }
                    },
                    prefill: {
                        name: user.displayName || '',
                        email: user.email || '',
                    },
                    theme: { color: "#4f46e5" }
                };

                // @ts-ignore
                const rzp = new window.Razorpay(options);
                rzp.open();

            } else {
                toast.error("Failed to generate payment params");
                setIsProcessing(false);
            }
        } catch (error) {
            console.error(error);
            toast.error("An error occurred while initiating subscription");
            setIsProcessing(false);
        }
    };

    const handleCancel = async () => {
        if (!user) return;
        setIsProcessing(true);
        try {
            await SubscriptionApiService.cancelSubscription();
            toast.success("Subscription cancelled successfully. Access remains until end of cycle.");
        } catch (error) {
            console.error(error);
            toast.error("Failed to cancel subscription");
        } finally {
            setIsProcessing(false);
        }
    };

    if (loading || !pricingConfig) {
        return (
            <DashboardLayout>
                <div className="flex min-h-screen items-center justify-center bg-background">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="bg-background flex-1 min-h-screen py-10 sm:py-16 overflow-hidden relative">
                {/* Background decorations */}
                <div className="absolute top-0 -left-4 w-72 h-72 bg-primary/20 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob pointer-events-none"></div>
                <div className="absolute top-0 -right-4 w-72 h-72 bg-secondary/30 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-2000 pointer-events-none"></div>

                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">

                    {/* Active Subscription & Credits Section */}
                    {subscription && (
                        <ActiveSubscriptionBanner
                            subscription={subscription}
                            creditBalance={creditBalance}
                            isActive={isActive}
                            isPending={isPending}
                            isProcessing={isProcessing}
                            onCancel={handleCancel}
                        />
                    )}

                    {!isActive && !isPending && (
                        <div className="text-center max-w-3xl mx-auto mb-12">
                            <h2 className="text-base font-semibold text-primary tracking-wide uppercase">Pricing</h2>
                            <p className="mt-2 text-4xl font-extrabold text-foreground sm:text-5xl">
                                Supercharge Your Creativity
                            </p>
                            <p className="mx-auto mt-5 max-w-xl text-lg text-muted-foreground">
                                Get access to premium AI generation tools for screenplays, storyboards, and shotlists.
                            </p>
                        </div>
                    )}

                    {/* Billing Toggle */}
                    <BillingToggle isYearly={isYearly} onChange={setIsYearly} />

                    <div className="mt-16 space-y-12 lg:space-y-0 lg:grid lg:grid-cols-3 lg:gap-x-8 max-w-7xl mx-auto">
                        {pricingConfig.plans.map((plan) => (
                            <PricingCard
                                key={plan.id}
                                plan={plan}
                                isYearly={isYearly}
                                subscription={subscription}
                                isActive={isActive}
                                isPending={isPending}
                                isProcessing={isProcessing}
                                currencySymbol={pricingConfig.currencySymbol}
                                onSubscribe={handleSubscribe}
                            />
                        ))}
                    </div>

                    {/* Subtle Footer Cancellation */}
                    {subscription && (subscription.status === 'active' || subscription.status === 'created') && (
                        <div className="mt-20 text-center border-t border-border/30 pt-8">
                            <p className="text-sm text-muted-foreground mb-2">Looking to make changes to your plan?</p>
                            <button
                                onClick={handleCancel}
                                disabled={isProcessing}
                                className="text-xs font-medium text-primary hover:text-destructive hover:underline transition-all duration-300"
                            >
                                {isProcessing && <Loader2 className="h-3 w-3 animate-spin inline mr-1" />}
                                Manage Or Cancel Subscription
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </DashboardLayout>
    );
};

export default PricingPage;
